import { motion } from "framer-motion";
import { ImageIcon } from "lucide-react";
import { useRegister } from "./RegisterContext";

const EASE = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, className = "", y = 28, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.8, delay, ease: EASE }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const CtaButton = ({ testId, line1 = "CLICK HERE TO SECURE YOUR FREE SEAT", line2 = "Join The Wealth Code Masterclass", className = "" }) => {
  const { openForm } = useRegister();
  return (
    <motion.button
      type="button"
      onClick={openForm}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.97 }}
      data-testid={testId}
      className={`cta-btn cta-pulse w-full rounded-2xl px-5 py-3.5 sm:py-4 text-white text-center ${className}`}
    >
      <span className="block font-extrabold tracking-wide text-[14px] sm:text-[17px] leading-tight">{line1}</span>
      {line2 && <span className="block text-[11px] sm:text-[12.5px] font-medium opacity-90 mt-0.5">{line2}</span>}
    </motion.button>
  );
};

export const Highlight = ({ children, tone = "orange" }) => (
  <span className={`hl hl-${tone}`}>{children}</span>
);

export const SectionTitle = ({ children, className = "" }) => (
  <h2 className={`font-black uppercase text-center text-[22px] sm:text-[30px] lg:text-[34px] leading-[1.2] tracking-tight text-ink ${className}`}>
    {children}
  </h2>
);

export const ImageSlot = ({ src, label, alt, testId, className = "", dark = false, position = "center" }) =>
  src ? (
    <img src={src} alt={alt || label} loading="lazy" style={{ objectPosition: position }} data-testid={testId} className={`w-full h-full object-cover ${className}`} />
  ) : (
    <div
      data-testid={testId}
      className={`w-full h-full flex flex-col items-center justify-center gap-2 text-center p-4 border-2 border-dashed ${dark ? "border-white/40 text-white/80 bg-white/5" : "border-plum/40 text-plum bg-plum/5"} ${className}`}
    >
      <ImageIcon className="w-8 h-8" />
      <span className="text-[11px] font-bold tracking-[0.2em] uppercase">Image slot</span>
      <span className="text-xs font-medium max-w-[16rem]">{label}</span>
    </div>
  );

export const Chevrons = () => (
  <div className="flex justify-center gap-6 py-6 text-plum/30" aria-hidden>
    {[0, 1, 2].map((i) => (
      <motion.svg key={i} width="26" height="20" viewBox="0 0 26 20" animate={{ y: [0, 5, 0] }} transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.15 }}>
        <path d="M2 2l11 8 11-8M2 10l11 8 11-8" stroke="currentColor" strokeWidth="2" fill="none" />
      </motion.svg>
    ))}
  </div>
);

export const Logo = ({ className = "w-6 h-6" }) => (
  <svg viewBox="0 0 64 64" className={className} aria-hidden>
    <rect width="64" height="64" rx="16" fill="#5B21D6" />
    <rect x="14" y="36" width="8" height="14" rx="2" fill="#fff" />
    <rect x="28" y="26" width="8" height="24" rx="2" fill="#fff" />
    <rect x="42" y="16" width="8" height="34" rx="2" fill="#FF6B1A" />
    <circle cx="46" cy="10" r="0" fill="#FF6B1A" />
  </svg>
);
